/* Equaliv · comportamentos base do tema.
   Um custom element por comportamento; as sections conversam por eventos:
   - eq:cart:updated   (detail: cart)  depois de qualquer alteração no carrinho
   - eq:cart:open                      abre o carrinho lateral
   - eq:fav:changed    (detail: ids)   favoritos alterados                           */
(() => {
  if (window.EQ) return;
  const EQ = (window.EQ = {});
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  EQ.settings = window.EQ_SETTINGS || {};

  /* ---------- utilidades ---------- */
  EQ.money = (cents) => {
    const v = (Number(cents) || 0) / 100;
    return 'R$ ' + v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  EQ.installments = (cents, short) => {
    const max = Number(EQ.settings.installmentsMax || 6);
    const min = Number(EQ.settings.installmentMinCents || 500);
    let n = Math.min(max, Math.floor((Number(cents) || 0) / min));
    if (n < 2) return '';
    return ((short ? EQ.settings.installmentsShort : EQ.settings.installmentsText) || 'ou [n]x de [valor] sem juros')
      .replace('[n]', n)
      .replace('[valor]', EQ.money(Math.round(cents / n)));
  };
  EQ.lock = (on) => document.documentElement.classList.toggle('eq-lock', !!on);
  EQ.toast = (msg) => {
    let t = document.querySelector('.eq-toast');
    if (!t) {
      t = document.createElement('div');
      t.className = 'eq-toast eq';
      t.setAttribute('role', 'status');
      document.body.appendChild(t);
    }
    t.textContent = msg;
    t.classList.add('is-on');
    clearTimeout(t._t);
    t._t = setTimeout(() => t.classList.remove('is-on'), 2600);
  };
  EQ.emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail }));

  /* ---------- assinatura (app Vindi Assinaturas): propriedades da linha ----------
     Toda linha com plano leva `_vindi_selling_plan` (id do plano) e `Plano` ("Assinatura 1 · a cada mês");
     sem plano, as duas saem. A linha entra no carrinho SEM selling plan da Shopify: o plano vai só nas propriedades
     (o app Vindi lê `_vindi_selling_plan` e aplica o desconto da assinatura). Rótulos vêm de snippets/eq-plan-registry (window.EQ_PLANS) ou do próprio carrinho. */
  EQ.PLAN_ID_KEY = '_vindi_selling_plan';
  EQ.PLAN_LABEL_KEY = 'Plano';
  EQ.plans = {};
  EQ.registerPlans = (map) => { Object.assign(EQ.plans, map || {}); };
  EQ.registerPlans(window.EQ_PLANS);
  EQ.planLabelFrom = (sp) => {
    if (!sp) return '';
    const fq = sp.options && sp.options[0] && sp.options[0].value;
    return (sp.name || '') + (fq ? ' · a cada ' + fq : '');
  };
  /** Devolve as propriedades da linha com (ou sem) as do plano. `label` opcional (senão usa o registro). */
  EQ.planProps = (props, planId, label) => {
    const out = { ...(props || {}) };
    delete out[EQ.PLAN_ID_KEY];
    delete out[EQ.PLAN_LABEL_KEY];
    if (planId) {
      out[EQ.PLAN_ID_KEY] = String(planId);
      const l = label || EQ.plans[String(planId)];
      if (l) out[EQ.PLAN_LABEL_KEY] = l;
    }
    return out;
  };
  const withPlanProps = (items) => (items || []).map((it) => {
    if (!it || !it.selling_plan) return it; // sem plano novo: as propriedades seguem como vieram (ex.: troca de variante mantém o plano)
    const { selling_plan: planId, ...rest } = it;
    const keep = it.properties && it.properties[EQ.PLAN_ID_KEY] === String(planId) ? it.properties[EQ.PLAN_LABEL_KEY] : '';
    return { ...rest, properties: EQ.planProps(it.properties, planId, EQ.plans[String(planId)] || keep) };
  });
  EQ.withPlanProps = withPlanProps;
  /** Linha com plano (pela propriedade) ainda sem o rótulo `Plano`. */
  EQ.needsPlanLabel = (it) => {
    const p = (it && it.properties) || {};
    return !!(p[EQ.PLAN_ID_KEY] || (it && it.selling_plan_allocation)) && !p[EQ.PLAN_LABEL_KEY];
  };
  /** Depois de adicionar: linhas com plano sem `Plano` (rótulo não registrado na página) recebem o rótulo, lido do
      produto (/products/<handle>.js) — a linha não tem selling plan da Shopify, só a propriedade com o id. */
  EQ.fixPlanProps = async (lines) => {
    for (const it of lines || []) {
      if (!EQ.needsPlanLabel(it)) continue;
      const p = it.properties || {};
      const id = String(p[EQ.PLAN_ID_KEY] || it.selling_plan_allocation.selling_plan.id);
      let label = EQ.plans[id];
      if (!label && it.selling_plan_allocation) label = EQ.planLabelFrom(it.selling_plan_allocation.selling_plan);
      if (!label && it.handle) {
        try {
          const prod = await fetch(root + 'products/' + it.handle + '.js').then((r) => r.json());
          for (const g of prod.selling_plan_groups || []) for (const sp of g.selling_plans || []) if (String(sp.id) === id) label = EQ.planLabelFrom(sp);
        } catch (_) {}
      }
      if (!label) continue;
      EQ.plans[id] = label;
      await fetch(root + 'cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: it.key, quantity: it.quantity, properties: EQ.planProps(p, id, label) }) });
    }
  };

  /* ---------- carrinho (Ajax API + Section Rendering) ---------- */
  const CART_SECTIONS = ['eq-cart-drawer'];
  EQ.cart = {
    async request(path, body) {
      if (body && path === 'cart/add.js' && body.items) body = { ...body, items: withPlanProps(body.items) };
      const res = await fetch(root + path, {
        method: body ? 'POST' : 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body ? JSON.stringify({ ...body, sections: CART_SECTIONS.join(','), sections_url: location.pathname }) : undefined,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Erro no carrinho');
      return data;
    },
    async add(items, { open = true } = {}) {
      let data = await this.request('cart/add.js', { items });
      const added = data.items || [];
      if (added.some(EQ.needsPlanLabel)) {
        await EQ.fixPlanProps(added);
        data = await this.request('cart/update.js', {});
      }
      await this.refresh(data.sections);
      if (open) {
        /* Configurações do tema › Equaliv · Carrinho › "Ao adicionar": não abrir = só a mensagem (o contador do ícone atualiza) */
        if (EQ.settings.cartType === 'none') EQ.toast(EQ.settings.addedText || 'Produto adicionado ao carrinho');
        else EQ.emit('eq:cart:open');
      }
      return data;
    },
    async change(line, quantity) {
      const data = await this.request('cart/change.js', { line, quantity });
      await this.refresh(data.sections, data);
      return data;
    },
    async update(updates, attributes) {
      const data = await this.request('cart/update.js', { updates, attributes });
      await this.refresh(data.sections, data);
      return data;
    },
    async refresh(sections, cart) {
      const paintSections = (map) => {
        for (const id of Object.keys(map || {})) {
          if (!map[id]) continue;
          const doc = new DOMParser().parseFromString(map[id], 'text/html');
          document.querySelectorAll(`[data-eq-section="${id}"]`).forEach((target) => {
            const fresh = doc.querySelector(`[data-eq-section="${id}"]`);
            if (fresh) target.innerHTML = fresh.innerHTML;
          });
        }
      };
      paintSections(sections);
      if (!cart) cart = await fetch(root + 'cart.js', { cache: 'no-store' }).then((r) => r.json());
      /* A Shopify às vezes devolve, na resposta do cart/add, o HTML do carrinho ANTES de gravar o item (carrinho lateral
         vazio com o contador já em 1). Se o carrinho lateral não bate com o cart.js, pede o HTML de novo, sem cache. */
      const box = document.querySelector('[data-eq-section="eq-cart-drawer"]');
      const want = (cart.items || []).length;
      if (box && box.querySelectorAll('[data-eq-cl]').length !== want) {
        try {
          const fresh = await fetch(`${location.pathname}?sections=eq-cart-drawer&_=${Date.now()}`, { cache: 'no-store' }).then((r) => r.json());
          paintSections(fresh);
        } catch (_) {}
      }
      EQ.emit('eq:cart:updated', cart);
      return cart;
    },
  };

  /* botões [data-eq-add] — variante única ou lista de itens */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-eq-add]');
    if (!btn) return;
    e.preventDefault();
    let items;
    try {
      items = btn.dataset.eqItems ? JSON.parse(btn.dataset.eqItems) : null;
    } catch (_) {}
    if (!items) {
      const form = btn.closest('form[data-eq-product-form]');
      const id = btn.dataset.variantId || (form && form.querySelector('[name="id"]').value);
      const qtyInput = form && form.querySelector('[name="quantity"]');
      const sp = form && form.querySelector('[name="selling_plan"]:checked, [name="selling_plan"][type="hidden"]');
      items = [{ id: Number(id), quantity: Number((qtyInput && qtyInput.value) || btn.dataset.quantity || 1) }];
      if (sp && sp.value) items[0].selling_plan = Number(sp.value);
    }
    if (btn.dataset.quickHandle && !btn.dataset.single) {
      EQ.quickAdd(btn.dataset.quickHandle);
      return;
    }
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    try {
      await EQ.cart.add(items);
    } catch (err) {
      EQ.toast(err.message);
    } finally {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
    }
  });

  /* ---------- abrir / fechar gavetas, modais e bottom sheets ---------- */
  EQ.open = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    const ov = document.querySelector(`[data-eq-overlay="${id}"]`);
    if (ov) ov.classList.add('is-open');
    EQ.lock(true);
    const focusable = el.querySelector('[data-eq-autofocus], button, a, input');
    if (focusable) setTimeout(() => focusable.focus({ preventScroll: true }), 60);
    EQ._last = id;
  };
  EQ.close = (id) => {
    const el = id ? document.getElementById(id) : document.querySelector('.is-open[aria-hidden="false"]');
    if (!el) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    const ov = document.querySelector(`[data-eq-overlay="${el.id}"]`);
    if (ov) ov.classList.remove('is-open');
    if (!document.querySelector('.eq-drawer.is-open, .eq-sheet.is-open')) EQ.lock(false);
  };
  document.addEventListener('click', (e) => {
    const o = e.target.closest('[data-eq-open]');
    if (o) {
      e.preventDefault();
      EQ.open(o.dataset.eqOpen);
      return;
    }
    const c = e.target.closest('[data-eq-close]');
    if (c) {
      e.preventDefault();
      EQ.close(c.dataset.eqClose || (c.closest('.eq-drawer, .eq-sheet') || {}).id);
      return;
    }
    const ov = e.target.closest('[data-eq-overlay]');
    if (ov) EQ.close(ov.dataset.eqOverlay);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      document.querySelectorAll('.eq-drawer.is-open, .eq-sheet.is-open').forEach((el) => EQ.close(el.id));
    }
  });
  document.addEventListener('eq:cart:open', () => {
    if (EQ.settings.cartType === 'page') {
      location.href = root + 'cart';
      return;
    }
    if (document.getElementById('EqCartDrawer')) EQ.open('EqCartDrawer');
  });

  /* ---------- quick add (modal / bottom sheet) ---------- */
  EQ.quickAdd = async (handle) => {
    const host = document.getElementById('EqQuickAdd');
    if (!host) return (location.href = root + 'products/' + handle);
    const body = host.querySelector('[data-eq-quick-body]');
    body.innerHTML = '<div class="eq-quick__loading" aria-busy="true"></div>';
    EQ.open('EqQuickAdd');
    try {
      const html = await fetch(`${root}products/${handle}?section_id=eq-quick-add`).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('[data-eq-quick-content]');
      body.innerHTML = content ? content.outerHTML : '';
      const qa = body.querySelector('[data-eq-qa]');
      if (qa) EQ.qa.init(qa);
    } catch (_) {
      location.href = root + 'products/' + handle;
    }
  };


  /* ---------- quick add V2 (sections/eq-quick-add.liquid) ----------
     Variante = combinação das opções; chips de quantidade = desconto progressivo (produto › loja);
     assinatura só quando a section mostra o card (selling plans). Adiciona com EQ.cart.add e fecha. */
  EQ.qa = {
    init(root) {
      let data;
      try {
        data = JSON.parse(root.querySelector('[data-eq-qa-json]').textContent);
      } catch (_) {
        return;
      }
      const $ = (sel) => root.querySelector(sel);
      const form = $('[data-eq-qa-form]');
      const idInput = $('[data-eq-qa-id]');
      const planInput = $('[data-eq-qa-plan]');
      const qty = $('[data-eq-qa-qty]');
      const submit = $('[data-eq-qa-submit]');
      const st = { variant: null, mode: 'once' };
      const selected = () => [...root.querySelectorAll('[data-eq-qa-option]')].map((fs) => {
        const c = fs.querySelector('input:checked');
        return c ? c.value : null;
      });
      const pickVariant = () => {
        const opts = selected();
        if (!opts.length) return data.variants[0];
        return data.variants.find((v) => v.options.every((o, i) => o === opts[i])) || null;
      };
      const tierRows = () => {
        const q = data.tiers.qtd || [];
        const p = data.tiers.pct || [];
        const rows = [{ n: 1, pct: 0 }];
        q.forEach((n, i) => rows.push({ n: Number(n), pct: Number(p[i] || 0) }));
        return rows.sort((a, b) => a.n - b.n).slice(0, data.chips || 3);
      };
      const paintTiers = () => {
        const box = $('[data-eq-qa-tiers]');
        if (!box || !st.variant) return;
        const cur = Number(qty.value || 1);
        box.innerHTML = tierRows()
          .map((r) => {
            const full = st.variant.price * r.n;
            const net = Math.round(full * (1 - r.pct / 100));
            const label = r.n + ' ' + (r.n === 1 ? data.unit : data.units);
            return `<button type="button" class="eq-qa__tier${cur === r.n ? ' is-active' : ''}" data-eq-qa-tier="${r.n}" aria-pressed="${cur === r.n}">${r.pct ? `<span class="eq-pill-off">-${r.pct}%</span>` : ''}<span class="eq-qa__tier-title">${label}</span>${r.pct ? `<span class="eq-qa__tier-from">${EQ.money(full)}</span>` : ''}<span class="eq-qa__tier-price">${EQ.money(net)}</span></button>`;
          })
          .join('');
      };
      /* tamanhos: só os que existem com as outras opções escolhidas (ex.: o sabor); texto "doses · preço" da variante;
         se o tamanho marcado sumir, marca o primeiro disponível */
      const sizeSets = [...root.querySelectorAll('[data-eq-qa-option]')].filter((fs) => fs.querySelector('.eq-qa__sizes'));
      const syncSizes = () => {
        sizeSets.forEach((fs) => {
          const i = Number(fs.dataset.eqQaOption);
          const opts = selected();
          let first = null, firstAvail = null, lost = false;
          fs.querySelectorAll('input').forEach((inp) => {
            const matches = data.variants.filter((x) => x.options.every((o, k) => (k === i ? o === inp.value : opts[k] == null || o === opts[k])));
            const vv = matches.find((x) => x.available) || matches[0];
            const lab = inp.closest('label');
            lab.style.display = vv ? '' : 'none';
            inp.disabled = !vv;
            if (!vv) { if (inp.checked) lost = true; return; }
            const sub = lab.querySelector('.eq-qa__size-sub');
            if (sub) sub.textContent = (vv.rotulo ? vv.rotulo + ' · ' : '') + EQ.money(vv.price);
            if (!first) first = inp;
            if (!firstAvail && vv.available) firstAvail = inp;
          });
          if (lost || !fs.querySelector('input:checked')) { const t = firstAvail || first; if (t) t.checked = true; }
        });
      };
      const paint = () => {
        syncSizes();
        const v = (st.variant = pickVariant());
        root.querySelectorAll('[data-eq-qa-option]').forEach((fs, i) => {
          fs.querySelectorAll('input').forEach((inp) => {
            const opts = selected();
            opts[i] = inp.value;
            /* tamanho: depende do sabor escolhido; sabor e demais opções: só apagado se nenhuma variante com ele estiver disponível */
            const ok = sizeSets.includes(fs) ? data.variants.some((x) => x.available && x.options.every((o, k) => o === opts[k])) : data.variants.some((x) => x.available && x.options[i] === inp.value);
            inp.closest('label').classList.toggle('is-unavailable', !ok);
          });
        });
        if (!v) {
          if (EQ.notify) EQ.notify.set(submit, null);
          submit.disabled = true;
          return;
        }
        idInput.value = v.id;
        const price = $('[data-eq-qa-price]');
        if (price) price.textContent = EQ.money(v.price);
        /* desconto automático: "de" riscado (o preço da variante já vem com o % no JSON do quick add) */
        const cmpEl = $('[data-eq-qa-compare]');
        if (cmpEl) { cmpEl.hidden = !(v.compare > v.price); cmpEl.textContent = v.compare > v.price ? EQ.money(v.compare) : ''; }
        const inst = $('[data-eq-qa-inst]');
        if (inst) inst.textContent = EQ.installments(v.price, true);
        const img = root.querySelector('.eq-qa-img');
        if (img && v.image) {
          img.src = v.image;
          img.removeAttribute('srcset');
        }
        const stock = $('[data-eq-qa-stock]');
        if (stock) stock.hidden = !(v.inventory !== null && v.inventory > 0 && v.inventory <= Number(EQ.settings.lowStock || 5));
        const pin = root.querySelector('input[name="eq-qa-plan"]:checked');
        const plan = v.plans && ((pin && v.plans.find((x) => String(x.id) === pin.value)) || v.plans[0]);
        const subP = $('[data-eq-qa-sub-price]');
        const subC = $('[data-eq-qa-sub-compare]');
        if (subP) subP.textContent = plan ? EQ.money(plan.price) : '';
        if (subC) subC.textContent = plan && plan.compare > plan.price ? EQ.money(plan.compare) : '';
        /* esgotado: "Avise-me" (assets/eq-notify.js) */
        const ntf = !v.available && EQ.notify;
        if (ntf) EQ.notify.set(submit, { id: v.id, title: submit.dataset.ntfTitle, variant: v.options.filter((o) => o && o !== 'Default Title').join(' / '), img: v.image || submit.dataset.ntfImg, url: submit.dataset.ntfUrl });
        else if (EQ.notify) EQ.notify.set(submit, null);
        submit.disabled = !v.available && !ntf;
        const t = submit.querySelector('[data-eq-qa-submit-text]');
        if (t) t.textContent = v.available ? submit.dataset.label : ntf ? EQ.notify.label(v.id) : submit.dataset.soldout;
        paintTiers();
      };
      const setMode = (mode) => {
        st.mode = mode;
        root.querySelectorAll('[data-eq-qa-freq]').forEach((c) => {
          const on = c.dataset.eqQaFreq === mode;
          c.classList.toggle('is-active', on);
          const r = c.querySelector('.eq-radio');
          if (r) r.classList.toggle('is-on', on);
          const h = c.querySelector('[data-eq-qa-pick]');
          if (h) h.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        const p = root.querySelector('input[name="eq-qa-plan"]:checked');
        planInput.value = mode === 'sub' && p ? p.value : '';
      };
      root.addEventListener('change', (e) => {
        if (e.target.name && e.target.name.startsWith('eq-qa-o')) paint();
        if (e.target.name === 'eq-qa-plan') { setMode('sub'); paint(); }
        if (e.target === qty) paintTiers();
      });
      root.addEventListener('click', (e) => {
        const pick = e.target.closest('[data-eq-qa-pick]');
        if (pick) setMode(pick.dataset.eqQaPick);
        const tier = e.target.closest('[data-eq-qa-tier]');
        if (tier) {
          qty.value = tier.dataset.eqQaTier;
          paintTiers();
        }
      });
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (!st.variant || submit.disabled) return;
        const item = { id: st.variant.id, quantity: Math.max(1, Number(qty.value || 1)) };
        if (planInput.value) {
          item.selling_plan = Number(planInput.value);
          const pin = root.querySelector('input[name="eq-qa-plan"]:checked');
          item.properties = EQ.planProps({}, item.selling_plan, pin && pin.dataset.planLabel);
        }
        submit.classList.add('is-loading');
        submit.setAttribute('aria-busy', 'true');
        try {
          EQ.close('EqQuickAdd');
          await EQ.cart.add([item]);
        } catch (err) {
          EQ.toast(err.message || EQ.settings.cartErrorText);
        } finally {
          submit.classList.remove('is-loading');
          submit.removeAttribute('aria-busy');
        }
      });
      paint();
      setMode('once');
    },
  };

  /* ---------- favoritos (regra 10: fonte = customer.metafields.equaliv.produtos_favoritos) ----------
     O coração nunca grava no navegador. Clique → página da extensão de conta
     (Configurações do tema › Equaliv · Favoritos › eq_fav_add_url) com ?add=<id>&return=<url>
     (ou ?remove=<id> quando já é favorito). A página exige login, grava o metafield e volta.
     O estado inicial vem do Liquid (aria-pressed) e de EQ_SETTINGS.favs (ids do metafield). */
  EQ.favs = {
    get() {
      return (EQ.settings.favs || []).map(String);
    },
    has(id) {
      return this.get().includes(String(id));
    },
    url(id, remove) {
      const base = EQ.settings.favAddUrl;
      const back = location.pathname + location.search;
      if (!base) {
        const login = EQ.settings.loginUrl || '/account/login';
        return EQ.settings.loggedIn ? (EQ.settings.favPageUrl || '/account') : login + '?return_url=' + encodeURIComponent(back);
      }
      const u = new URL(base, location.origin);
      u.searchParams.set(remove ? 'remove' : 'add', id);
      u.searchParams.set('return', back);
      // A página de favoritos da conta nova fica noutro domínio (shopify.com/<id>/account/pages/<uuid>):
      // URL absoluta mantém o domínio; caminho relativo continua relativo.
      return u.origin === location.origin ? u.pathname + u.search + u.hash : u.href;
    },
    /* Com o app proxy (Configurações do tema › eq_fav_proxy, ex. /apps/eqfav) o clique grava sem sair da página:
       a Shopify identifica o cliente logado e o app grava o mesmo metafield. Sem proxy, sem login ou se o
       proxy falhar, segue o caminho antigo (página da extensão). */
    async toggle(id) {
      const remove = this.has(id);
      const proxy = EQ.settings.favProxy;
      if (!proxy || !EQ.settings.loggedIn) {
        location.href = this.url(id, remove);
        return;
      }
      document.querySelectorAll('[data-eq-fav="' + String(id).replace(/\D/g, '') + '"]').forEach((b) => b.setAttribute('aria-pressed', remove ? 'false' : 'true'));
      try {
        const r = await fetch(proxy + (proxy.indexOf('?') < 0 ? '?' : '&') + (remove ? 'remove=' : 'add=') + encodeURIComponent(id), {
          headers: { accept: 'application/json' },
          credentials: 'same-origin',
        });
        const d = await r.json();
        if (!r.ok || !d || !d.ok || !Array.isArray(d.ids)) throw new Error('fav');
        EQ.settings.favs = d.ids.map(String);
        paintFavs();
        EQ.emit('eq:fav:changed', EQ.settings.favs);
        EQ.toast(remove ? EQ.settings.favRemovedText || 'Removido dos favoritos' : EQ.settings.favAddedText || 'Adicionado aos favoritos');
      } catch (e) {
        location.href = this.url(id, remove);
      }
    },
  };
  const paintFavs = () => {
    const list = EQ.favs.get();
    document.querySelectorAll('[data-eq-fav]').forEach((b) => {
      const on = list.includes(String(b.dataset.eqFav));
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (b.dataset.labelOn && b.dataset.labelOff) b.setAttribute('aria-label', on ? b.dataset.labelOn : b.dataset.labelOff);
    });
    document.querySelectorAll('[data-eq-fav-count]').forEach((el) => {
      el.textContent = list.length;
      if (!el.hasAttribute('data-keep')) el.hidden = list.length === 0;
    });
  };
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-eq-fav]');
    if (!b) return;
    e.preventDefault();
    e.stopPropagation();
    if (b.classList.contains('is-busy')) return;
    b.classList.add('is-busy');
    Promise.resolve(EQ.favs.toggle(b.dataset.eqFav)).finally(() => {
      if (EQ.settings.favProxy) b.classList.remove('is-busy');
    });
  });
  EQ.paintFavs = paintFavs;
  document.addEventListener('eq:cart:updated', paintFavs);
  document.addEventListener('DOMContentLoaded', paintFavs);
  window.addEventListener('pageshow', () => document.querySelectorAll('[data-eq-fav].is-busy').forEach((b) => b.classList.remove('is-busy')));

  /* ---------- carrossel com barra de progresso segmentada ---------- */
  class EqCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-eq-track]');
      if (!this.track) return;
      this.prev = this.querySelector('[data-eq-prev]');
      this.next = this.querySelector('[data-eq-next]');
      this.bar = this.querySelector('[data-eq-bar]');
      this.dots = this.querySelector('[data-eq-dots]');
      this.prev && this.prev.addEventListener('click', () => this.go(-1));
      this.next && this.next.addEventListener('click', () => this.go(1));
      this.track.addEventListener('scroll', () => this.paint(), { passive: true });
      this.ro = new ResizeObserver(() => this.build());
      this.ro.observe(this.track);
      if (this.dataset.autoplay && !matchMedia('(prefers-reduced-motion: reduce)').matches) this.auto(Number(this.dataset.autoplay) * 1000);
    }
    disconnectedCallback() {
      this.ro && this.ro.disconnect();
      clearInterval(this.timer);
    }
    pages() {
      const t = this.track;
      return Math.max(1, Math.round(t.scrollWidth / Math.max(1, t.clientWidth) + 0.49) - (t.scrollWidth <= t.clientWidth + 2 ? 0 : 0));
    }
    build() {
      const t = this.track;
      const scrollable = t.scrollWidth > t.clientWidth + 4;
      this.classList.toggle('is-static', !scrollable);
      const items = t.children.length;
      const rawSegs = this.bar ? this.bar.dataset.segments || 'auto' : 0;
      const segs = this.bar ? (rawSegs === 'auto' ? Math.max(2, Math.min(12, items)) : Number(rawSegs)) : 0;
      if (this.bar && this.bar.children.length !== segs) {
        this.bar.innerHTML = Array.from({ length: segs }, () => '<span class="eq-progress__seg"></span>').join('');
      }
      if (this.dots) {
        const n = Math.min(Number(this.dots.dataset.max || 8), Math.max(1, items));
        if (this.dots.children.length !== n) {
          this.dots.innerHTML = Array.from({ length: n }, (_, i) => `<button type="button" class="eq-dot" aria-label="Ir para ${i + 1}"></button>`).join('');
          [...this.dots.children].forEach((d, i) =>
            d.addEventListener('click', () => {
              const max = t.scrollWidth - t.clientWidth;
              t.scrollTo({ left: (max * i) / Math.max(1, n - 1), behavior: 'smooth' });
            })
          );
        }
      }
      if (this.bar) this.bar.parentElement.hidden = !scrollable;
      if (this.dots) this.dots.hidden = !scrollable;
      this.paint();
    }
    ratio() {
      const t = this.track;
      const max = t.scrollWidth - t.clientWidth;
      return max <= 0 ? 0 : Math.min(1, Math.max(0, t.scrollLeft / max));
    }
    paint() {
      const r = this.ratio();
      if (this.bar) {
        const segs = [...this.bar.children];
        const active = Math.round(r * (segs.length - 1));
        segs.forEach((s, i) => {
          s.classList.toggle('is-active', i === active);
          s.classList.toggle('is-past', i < active);
        });
      }
      if (this.dots) {
        const ds = [...this.dots.children];
        const active = Math.round(r * (ds.length - 1));
        ds.forEach((d, i) => d.classList.toggle('is-active', i === active));
      }
      const t = this.track;
      if (this.prev) this.prev.disabled = t.scrollLeft <= 2;
      if (this.next) this.next.disabled = t.scrollLeft >= t.scrollWidth - t.clientWidth - 2;
    }
    go(dir) {
      const t = this.track;
      const gap = parseFloat(getComputedStyle(t).columnGap) || 16;
      const step = this.dataset.step !== 'page' && t.children[0] ? t.children[0].getBoundingClientRect().width + gap : t.clientWidth * 0.9;
      t.scrollBy({ left: dir * step, behavior: 'smooth' });
    }
    auto(ms) {
      const tick = () => {
        const t = this.track;
        if (t.scrollLeft >= t.scrollWidth - t.clientWidth - 2) t.scrollTo({ left: 0, behavior: 'smooth' });
        else this.go(1);
      };
      this.timer = setInterval(tick, ms);
      this.addEventListener('pointerenter', () => clearInterval(this.timer));
    }
  }
  customElements.define('eq-carousel', EqCarousel);

  /* ---------- slideshow (banner) — um slide por vez ---------- */
  class EqSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-eq-slide]')];
      this.dots = [...this.querySelectorAll('[data-eq-dot]')];
      this.i = 0;
      this.querySelectorAll('[data-eq-prev]').forEach((b) => b.addEventListener('click', () => this.show(this.i - 1)));
      this.querySelectorAll('[data-eq-next]').forEach((b) => b.addEventListener('click', () => this.show(this.i + 1)));
      this.dots.forEach((d, i) => d.addEventListener('click', () => this.show(i)));
      let x0 = null;
      this.addEventListener('touchstart', (e) => (x0 = e.touches[0].clientX), { passive: true });
      this.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        if (Math.abs(dx) > 40) this.show(this.i + (dx < 0 ? 1 : -1));
        x0 = null;
      });
      const sec = Number(this.dataset.autoplay || 0);
      if (sec > 0 && this.slides.length > 1 && !(window.Shopify && Shopify.designMode)) {
        this.timer = setInterval(() => this.show(this.i + 1), sec * 1000);
        this.addEventListener('pointerenter', () => clearInterval(this.timer));
      }
      this.show(0);
      document.addEventListener('shopify:block:select', (e) => {
        const idx = this.slides.findIndex((s) => s.contains(e.target));
        if (idx >= 0) this.show(idx);
      });
    }
    disconnectedCallback() {
      clearInterval(this.timer);
    }
    show(n) {
      if (!this.slides.length) return;
      this.i = (n + this.slides.length) % this.slides.length;
      this.slides.forEach((s, k) => {
        s.classList.toggle('is-active', k === this.i);
        s.setAttribute('aria-hidden', k === this.i ? 'false' : 'true');
      });
      this.dots.forEach((d, k) => d.classList.toggle('is-active', k === this.i));
    }
  }
  customElements.define('eq-slideshow', EqSlideshow);

  /* ---------- abas genéricas (chips que trocam painéis) ---------- */
  class EqTabs extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const tab = e.target.closest('[data-eq-tab]');
        if (!tab || !this.contains(tab)) return;
        e.preventDefault();
        this.select(tab.dataset.eqTab);
      });
      /* Performance: painéis com <template data-eq-remote> vazio são buscados pela Section Rendering API
         quando a seção se aproxima da tela (1000px antes), quando o cliente toca/passa o mouse nela,
         ou 4s depois do load — o que vier primeiro. Assim os produtos já estão prontos no primeiro clique. */
      if (this.dataset.eqRemoteSection && this.querySelector('template[data-eq-remote]')) {
        const go = () => this.loadRemote();
        ['pointerover', 'touchstart', 'focusin'].forEach((ev) => this.addEventListener(ev, go, { once: true, passive: true }));
        if ('IntersectionObserver' in window) {
          const io = new IntersectionObserver((en) => {
            if (en.some((x) => x.isIntersecting)) {
              io.disconnect();
              go();
            }
          }, { rootMargin: '1000px 0px' });
          io.observe(this);
        }
        const late = () => setTimeout(go, 4000);
        if (document.readyState === 'complete') late();
        else window.addEventListener('load', late, { once: true });
      }
    }
    loadRemote() {
      if (this._remote) return this._remote;
      const url = location.pathname + '?section_id=' + encodeURIComponent(this.dataset.eqRemoteSection);
      this._remote = fetch(url, { credentials: 'same-origin' })
        .then((r) => {
          if (!r.ok) throw new Error('remote');
          return r.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          this.querySelectorAll('[data-eq-panel]').forEach((p) => {
            const holder = p.querySelector('template[data-eq-remote]');
            if (!holder) return;
            const fresh = doc.querySelector(`[data-eq-panel="${p.dataset.eqPanel}"] template[data-eq-defer]`);
            if (!fresh) return;
            const t = document.createElement('template');
            t.setAttribute('data-eq-defer', '');
            t.content.appendChild(document.importNode(fresh.content, true));
            holder.replaceWith(t);
          });
          const open = this.querySelector('[data-eq-panel]:not([hidden])');
          if (open && open.querySelector('template[data-eq-defer]')) this.select(open.dataset.eqPanel);
        })
        .catch(() => {
          this._remote = null;
        });
      return this._remote;
    }
    select(key) {
      this.querySelectorAll('[data-eq-tab]').forEach((t) => {
        const on = t.dataset.eqTab === key;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      this.querySelectorAll('[data-eq-panel]').forEach((p) => (p.hidden = p.dataset.eqPanel !== key));
      if (this.querySelector('[data-eq-panel]:not([hidden]) template[data-eq-remote]')) {
        this.loadRemote();
        return;
      }
      const lazy = this.querySelectorAll('[data-eq-panel]:not([hidden]) template[data-eq-defer]');
      lazy.forEach((t) => t.replaceWith(document.importNode(t.content, true)));
      if (lazy.length && EQ.paintFavs) EQ.paintFavs();
      this.querySelectorAll('[data-eq-panel]:not([hidden]) eq-carousel').forEach((c) => c.build && c.build());
    }
  }
  customElements.define('eq-tabs', EqTabs);

  /* ---------- stepper de quantidade ---------- */
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-eq-qty]');
    if (!b) return;
    const wrap = b.closest('.eq-qty');
    const input = wrap && wrap.querySelector('input');
    if (!input) return;
    const min = Number(input.min || 1);
    const max = Number(input.max || 999);
    const v = Math.min(max, Math.max(min, Number(input.value || 1) + Number(b.dataset.eqQty)));
    input.value = v;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- vídeo com capa: ficheiro ou YouTube ---------- */
  document.addEventListener('click', (e) => {
    const p = e.target.closest('[data-eq-video]');
    if (!p) return;
    e.preventDefault();
    const box = p.parentElement;
    const yt = p.dataset.youtube;
    const src = p.dataset.src;
    if (yt) {
      const f = document.createElement('iframe');
      f.src = `https://www.youtube-nocookie.com/embed/${yt}?autoplay=1&rel=0&playsinline=1`;
      f.allow = 'autoplay; encrypted-media; picture-in-picture';
      f.allowFullscreen = true;
      f.title = p.getAttribute('aria-label') || 'Vídeo';
      box.appendChild(f);
    } else if (src) {
      const v = document.createElement('video');
      v.src = src;
      v.controls = true;
      v.autoplay = true;
      v.playsInline = true;
      box.appendChild(v);
      v.play().catch(() => {});
    }
    p.remove();
  });

  /* ---------- acordeão "um aberto por vez" ---------- */
  document.addEventListener(
    'toggle',
    (e) => {
      const d = e.target;
      if (!(d instanceof HTMLDetailsElement) || !d.open) return;
      const group = d.closest('[data-eq-accordion-single]');
      if (!group) return;
      group.querySelectorAll('details[open]').forEach((o) => o !== d && o.removeAttribute('open'));
    },
    true
  );

  /* ---------- select com label flutuante ---------- */
  document.addEventListener('change', (e) => {
    if (e.target.matches && e.target.matches('.eq-select')) e.target.classList.toggle('has-value', !!e.target.value);
  });

  /* ---------- ícone do carrinho: animação enquanto houver produtos (Configurações do tema › Equaliv · Carrinho) ---------- */
  (() => {
    let count = null;
    let timer = null;
    const icons = () => [...document.querySelectorAll('[data-eq-open="EqCartDrawer"]')].filter((a) => a.querySelector('[data-eq-cart-count]'));
    const secs = () => Number((document.getElementById('EqCartDrawer') || {}).dataset?.wiggle || 0);
    const start = () => {
      clearInterval(timer);
      timer = null;
      const s = secs();
      if (!s || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (count === null) {
        const b = document.querySelector('[data-eq-cart-count]');
        count = b ? Number(b.textContent) || 0 : 0;
      }
      if (count <= 0) return;
      timer = setInterval(() => {
        if (document.hidden) return;
        icons().forEach((a) => {
          a.classList.remove('eq-wiggle');
          void a.offsetWidth;
          a.classList.add('eq-wiggle');
        });
      }, s * 1000);
    };
    document.addEventListener('animationend', (e) => {
      if (e.target.classList && e.target.classList.contains('eq-wiggle')) e.target.classList.remove('eq-wiggle');
    });
    document.addEventListener('eq:cart:updated', (e) => {
      if (e.detail && typeof e.detail.item_count === 'number') count = e.detail.item_count;
      start();
    });
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
    else start();
  })();

  /* ---------- contador do carrinho no header ---------- */
  document.addEventListener('eq:cart:updated', (e) => {
    const n = e.detail && typeof e.detail.item_count === 'number' ? e.detail.item_count : null;
    if (n === null) return;
    document.querySelectorAll('[data-eq-cart-count]').forEach((el) => {
      el.textContent = n;
      el.hidden = n === 0;
    });
  });
})();
